import store from "@/vuex/store";

let registeredComponents = [];

/**
 * Intended to be used instead of $(document).ready(..).
 *
 * The callback will be fired on initial page load and any other subsequent
 * turbo visits. $(document).ready(...) only fires on the initial page load.
 *
 * The callback passed here will fire on every page load so ensure that the
 * callback is written in a way where this will not cause issues.
 *
 * @param {Function} callback Callback to fire
 */
window.readyListeners = [];

export const onReady = (callback) => {
  $(document).ready(callback);
  $(document).on("turbo:render", callback);
  window.readyListeners.push(callback);
};

export const triggerOnReady = () => {
  window.readyListeners.forEach((fn) => fn.call(this));
};

/**
 * Register a Vue component with turbo. This will trigger a destroy on the
 * component passed before turbo navigates to a new page.
 *
 * @param {Object} app Vue app instance (returned by createApp)
 * @param {Element} [node] The DOM node the app was mounted on, so it can be
 *   cleaned up (see the turbo:before-cache handler in initTurboEvents).
 */
export const registerVueComponent = (app, node) => {
  registeredComponents.push({ app, node });
};

/**
 * Best-effort synchronous upgrade of any not-yet-upgraded custom elements in
 * `root`. Guarded so a throwing connectedCallback can never break the Turbo
 * render path, and callers scope `root` to the rendered subtree to avoid
 * walking the whole document.
 *
 * @param {Element|Document} root Subtree to upgrade
 */
const upgradeCustomElements = (root) => {
  if (!root) return;

  try {
    customElements.upgrade(root);
  } catch {
    // no-op: upgrading is best-effort and must never break rendering
  }
};

/**
 * Unmounts every registered Vue app and strips the `data-v-app` attribute Vue
 * leaves on its mount node, so a later renderVueComponent() call is free to
 * remount a fresh instance instead of skipping it (see the `data-v-app` check
 * in renderVueComponent.js).
 *
 * Tearing an app down this way is what lets Vue's own Transition/Teleport
 * teardown guard (state.isUnmounting) take over: an unmount() call here is
 * Vue-driven, so any in-flight Transition on that app resolves immediately
 * and safely instead of leaving a callback to fire later against DOM Turbo
 * has since discarded.
 */
const unmountRegisteredComponents = () => {
  registeredComponents.forEach(({ app, node }) => {
    try {
      app?.unmount?.();
    } catch (e) {
      // App may already be unmounted; nothing to clean up.
    }
    node?.removeAttribute?.("data-v-app");
  });
  registeredComponents = [];
};

/**
 * Initializes turbo navigation events to do the following:
 *
 * 1. Destroy all registered components and reset the Vuex store before navigating to a new page.
 * 2. Mark in-app navigation pages as needing a refresh when navigating away.
 * 3. Refresh pages marked as needing a refresh when using back/forward button.
 * 4. Catch abort errors when in-progress requests are cancelled (navigating away).
 */
export const initTurboEvents = () => {
  // Add a custom header to identify turbo requests
  $(document).on("turbo:before-fetch-request", (e) => {
    const event = e.originalEvent;
    event.preventDefault();
    event.detail.fetchOptions.headers["Turbo-Drive"] = "true";
    event.detail.resume();
  });

  // Destroy all registered components and reset the Vuex store before each render.
  //
  // This is the only teardown hook Turbo guarantees on every full-document
  // visit, including browser back/forward (Turbo's own History observer
  // turns every popstate into a real Visit, independent of any in-app click
  // handling). The turbo:before-cache handler below looks like a second
  // safety net for this, but every layout sets
  // <meta name="turbo-cache-control" content="no-cache">, which makes Turbo
  // skip snapshot caching -- and the turbo:before-cache event -- entirely for
  // the outgoing page. So unmounting here is not optional cleanup, it is the
  // only place any of this actually runs.
  $(document).on("turbo:before-render", (e) => {
    const event = e.originalEvent;
    event.preventDefault();

    unmountRegisteredComponents();

    store.commit("reset");
    event.detail.resume();
  });

  // Unmount registered Vue apps before Turbo caches the outgoing page.
  //
  // Components like DatatableV3 teleport their search/filter inputs into
  // DataTables-managed header cells that live outside the Vue root. Without a
  // teardown here, those teleported nodes get frozen into Turbo's cached
  // snapshot; on the next mount (forward nav, or a back/forward restore) a
  // fresh set of inputs is teleported on top of the stale ones, stacking 2-3
  // duplicate search boxes. Unmounting triggers each app's onBeforeUnmount ->
  // destroyDatatable(), which removes that DOM, so the snapshot stays clean.
  //
  // Kept as defense-in-depth for any page that opts back into caching --
  // turbo:before-render above already covers the common case, since this
  // event does not fire at all on a page marked turbo-cache-control: no-cache
  // (which is every page today). See TOF-612.
  $(document).on("turbo:before-cache", unmountRegisteredComponents);

  // Check before a turbo visit if the previous page was in-app navigation.
  // If so, mark it to refresh if the user hits the back button.
  $(document).on("turbo:before-visit", () => {
    if (history.state?.turbo === undefined) {
      const state = { ...history.state, turboRefresh: true };
      history.replaceState(state, "");
    }
  });

  // Only show turbo frame loader on initial load
  $(document).on("turbo:frame-load", (e) => {
    const frame = e.target;
    const container = frame.closest(".initial-load");
    if (!container) return;

    container.classList.remove("initial-load");
  });

  // Turbo full-page and frame renders can inject sera-web-ui custom elements.
  // Upgrade them eagerly so their imperative API (getValue/setValue) is ready
  // for any controller that reads it on connect, rather than each call site
  // having to defend against a not-yet-upgraded element. Scoped to the rendered
  // subtree (whole body on a page render; just the frame on a frame load).
  $(document).on("turbo:render", () => upgradeCustomElements(document.body));
  $(document).on("turbo:frame-load", (e) => upgradeCustomElements(e.target));

  // Reinitialize Parsley validation after turbo stream replace action
  // This happens when forms are re-rendered after validation errors
  document.addEventListener("turbo:before-stream-render", (e) => {
    const { render } = e.detail;

    // Store original render function
    const originalRender = render;

    // Override render to add callback after replacement
    e.detail.render = function (streamElement) {
      originalRender(streamElement);

      // Streamed markup can contain sera-web-ui custom elements the browser has
      // not upgraded yet. Force a synchronous upgrade so their imperative API
      // (getValue/setValue) exists before the Stimulus value-changed callbacks
      // that this render triggers read it — otherwise the first read throws
      // "getValue is not a function" and the triggered calculation aborts.
      // Scoped to the stream's target(s) so we don't walk the whole document.
      const streamRoots = streamElement.targetElements?.length
        ? streamElement.targetElements
        : [document.body];
      streamRoots.forEach(upgradeCustomElements);

      // After replacement, reinitialize Parsley on any forms
      setTimeout(() => {
        const $forms = $(".parsley-validations");
        if ($forms.length) {
          $forms.each(function () {
            // Destroy existing parsley instance if it exists
            const parsleyInstance = $(this).parsley();
            if (parsleyInstance) {
              parsleyInstance.destroy();
            }

            // Reinitialize parsley
            $(this).parsley({
              excluded:
                "input[type=button], input[type=submit], input[type=reset], input[type=hidden], [disabled], :hidden",
              successClass: "parsley-success-input"
            });
          });
        }
      }, 0);
    };
  });

  // Trigger a turbo refresh if the page being navigated to requires it.
  $(window).on("popstate", (e) => {
    const { state } = e.originalEvent;
    if (state?.turboRefresh) {
      window.Turbo.visit(window.location.href, { action: "replace" });
    }
  });

  // Catch Abort errors when Turbo requests are cancelled (navigating away)
  $(window).on("unhandledrejection", (e) => {
    const event = e.originalEvent;
    if (event.reason?.name === "AbortError") {
      event.preventDefault();
    }
  });
};

/**
 * Returns the state params to use for turbo when updating the URL through pushState or replaceState.
 *
 * @param {Object} currentState The state of the current history entry
 * @param {Boolean} replace If the current history entry is being replaced
 *
 * @returns {Object} State object
 */
export const turboState = (currentState, replace) => {
  const state = {};
  if (!replace) return state;

  // The turboRefresh state param causes the URL to be refreshed by turbo when navigating back/foward.
  // If the URL being replaced was loaded by turbo or if turboRefresh was already set, set turboRefresh.
  // See the popstate handler in initTurboEvents for details.
  if (currentState.turbo || currentState.turboRefresh) {
    state.turboRefresh = true;
  }

  return state;
};
